import type { CampaignFeasibilityResponse } from '../../../services/feasibilityService';

export function formatMonthLabel(isoDate: string): string {
  // Use UTC so "2026-08-01" doesn't drift to July in negative-offset tz.
  const d = new Date(isoDate + 'T00:00:00Z');
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

export function uniqueMonthsFromFlights(
  verdict: CampaignFeasibilityResponse,
  flightIndexes: number[],
): string[] {
  const seen = new Set<string>();
  for (const idx of flightIndexes) {
    const flight = verdict.flightBreakdown[idx];
    if (!flight) continue;
    seen.add(formatMonthLabel(flight.startDate));
  }
  return Array.from(seen);
}

export function joinMonthsForCopy(months: string[]): string | null {
  if (months.length === 0) return null;
  if (months.length === 1) return months[0];
  if (months.length === 2) return `${months[0]} and ${months[1]}`;
  return `${months.slice(0, -1).join(', ')}, and ${months[months.length - 1]}`;
}

/**
 * CTV-474: render a calendar date as "Aug 19, 2026" copy for the Extension
 * date suggestion. Returns null when no date is provided.
 */
export function formatExtensionDateCopy(date: string | null): string | null {
  if (!date) return null;
  const d = new Date(date + 'T00:00:00Z');
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * CTV-502: campaign-totals copy used by InventoryWarningCard +
 * InventoryRegressionModal. Renders an **Availability label** — a
 * three-bucket qualitative string derived from the avail/requested ratio.
 * The raw percentage and impression counts are intentionally not shown to
 * the buyer; `requestedImpressionsTotal` / `netAvailableImpressionsTotal`
 * still flow on the API contract for feasibility math.
 */
export function formatTotalRatioCopy(
  verdict: CampaignFeasibilityResponse,
  stationNameOverride?: string,
): string {
  const station = stationNameOverride ?? verdict.stationName;
  const avail = Math.max(0, verdict.netAvailableImpressionsTotal);
  const req = Math.max(0, verdict.requestedImpressionsTotal);
  if (req <= 0) return `Inventory on ${station} is very limited.`;
  const ratio = avail / req;
  if (ratio < 0.25) return `Inventory on ${station} is very limited.`;
  if (ratio < 0.75) return `Inventory on ${station} is limited.`;
  return `Inventory on ${station} is tight.`;
}

/**
 * CTV-474: GEO coverage threshold below which the warning card switches
 * from silence ("geography isn't the issue") to the active "your selection
 * covers ~N%" copy + recommendation buttons. Matches
 * GEO_COVERAGE_LOW_THRESHOLD_PCT on the backend.
 */
export const GEO_COVERAGE_LOW_THRESHOLD_PCT = 75;

/**
 * CTV-474: full-name lookup for the two-letter state codes the wizard
 * exchanges with the backend. Used to render copy like
 * "Add Hawaii — KITV's home state" while keeping the wire format as 2-letter codes.
 */
export const US_STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'District of Columbia',
};

export function stateNameForCode(code: string): string {
  return US_STATE_NAMES[code.toUpperCase()] ?? code.toUpperCase();
}

/**
 * CTV-474: joins an array of labels for inline copy ("HI, OR, and CA").
 * Identical contract to joinMonthsForCopy; separate so future style tweaks
 * can diverge without touching the month version.
 */
export function joinLabelsForCopy(labels: string[]): string | null {
  if (labels.length === 0) return null;
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}
